import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { meAPI } from '../lib/api';
import { t } from '../lib/i18n';
import TaskDetailPanel from '../components/TaskDetailPanel';
import { PriorityDot, fmtDate } from '../components/ui';

const TABS = [
  { id: 'assigned', label: 'Assigned to me' },
  { id: 'created', label: 'Created by me' },
  { id: 'watching', label: 'Watching' },
];

const BUCKETS = [
  { id: 'overdue', label: 'Overdue' },
  { id: 'upcoming', label: 'Due this week' },
  { id: 'later', label: 'Later' },
  { id: 'no_date', label: 'No date' },
];

export default function MyWork() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('assigned');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = useCallback(() => {
    meAPI.tasks().then(({ data }) => setData(data));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // deep link from a notification: /my-work?task={id} opens the standalone panel
  useEffect(() => {
    const tid = searchParams.get('task');
    if (tid) {
      setOpenTaskId(tid);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!data) return null;
  const tasks = data[tab] || [];
  const projects = data.projects || {};

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-4 text-lg font-semibold">{t('My Work')}</h1>
      <div className="mb-4 flex gap-1">
        {TABS.map((tabDef) => (
          <button
            key={tabDef.id}
            type="button"
            onClick={() => setTab(tabDef.id)}
            className={`rounded-md px-2.5 py-1.5 text-sm ${
              tab === tabDef.id ? 'bg-pm-raised text-pm-text' : 'text-pm-muted hover:text-pm-text'
            }`}
          >
            {t(tabDef.label)}
            <span className="pm-num ml-1.5 text-xs text-pm-faint">{(data[tabDef.id] || []).length}</span>
          </button>
        ))}
      </div>

      {tasks.length === 0 && (
        <div className="rounded-lg border border-dashed border-pm-border py-16 text-center text-sm text-pm-muted">
          {t('Nothing here — enjoy the calm.')}
        </div>
      )}

      {BUCKETS.map((b) => {
        const items = tasks.filter((t) => t.bucket === b.id);
        if (!items.length) return null;
        return (
          <section key={b.id} className="mb-6">
            <h2
              className="mb-2 text-xs font-semibold uppercase tracking-wide"
              style={{ color: b.id === 'overdue' ? 'var(--pm-danger)' : 'var(--pm-faint)' }}
            >
              {t(b.label)} · <span className="pm-num">{items.length}</span>
            </h2>
            <ul className="divide-y divide-pm-border/60 rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]">
              {items.map((t) => {
                const p = projects[t.project_id];
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setOpenTaskId(t.id)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-pm-raised/60"
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p?.color || 'var(--pm-faint)' }} />
                      <span className="min-w-0 flex-1 truncate text-sm">{t.title}</span>
                      <span className="hidden text-xs text-pm-faint sm:inline">{p?.name}</span>
                      <PriorityDot priority={t.priority} />
                      {t.due_date && (
                        <span
                          className="text-xs"
                          style={{ color: t.bucket === 'overdue' ? 'var(--pm-danger)' : 'var(--pm-faint)' }}
                        >
                          {fmtDate(t.due_date)}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {openTaskId && (
        <TaskDetailPanel
          standalone
          taskId={openTaskId}
          readOnly={false}
          onClose={() => setOpenTaskId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}
